import { type AsyncOperationsState, asyncOperationsSchema } from './schema';

function readAsyncOperationsFromLocalStorage(key: string, initialValue: AsyncOperationsState): AsyncOperationsState {
  const stored = localStorage.getItem(key);

  if (!stored) {
    return initialValue;
  }

  try {
    const parsed = JSON.parse(stored);
    const result = asyncOperationsSchema.safeParse(parsed);

    return result.success ? result.data : initialValue;
  } catch {
    return initialValue;
  }
}

export const asyncOperationsLocalStorage = {
  getItem: (key: string, initialValue: AsyncOperationsState): AsyncOperationsState => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    return readAsyncOperationsFromLocalStorage(key, initialValue);
  },
  setItem: (key: string, value: AsyncOperationsState): void => {
    if (typeof window === 'undefined') {
      return;
    }

    // isPanelOpen is intentionally not persisted, it always resets to false on page load.
    const serialized = {
      nextAfter: value.nextAfter?.toString() ?? null,
      panelLastClosedAt: value.panelLastClosedAt?.toString() ?? null,
      hasUnreadEvents: value.hasUnreadEvents,
      eventIdsWithWarnings: value.eventIdsWithWarnings,
    };

    localStorage.setItem(key, JSON.stringify(serialized));
  },
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(key);
  },
  subscribe: (key: string, callback: (value: AsyncOperationsState) => void, initialValue: AsyncOperationsState) => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        callback(event.newValue ? readAsyncOperationsFromLocalStorage(key, initialValue) : initialValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  },
};
